import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { useTheme } from '../context/ThemeContext';
import { useAuth } from '../context/AuthContext';
import { openCookieSettings } from '../components/CookieConsent';

/* ─── animated counter ─── */
const Counter = ({ end, suffix = '', duration = 2000 }) => {
  const [val, setVal] = useState(0);
  const ref = useRef(null);
  useEffect(() => {
    const observer = new IntersectionObserver(([e]) => {
      if (e.isIntersecting) {
        let start = 0;
        const step = end / (duration / 16);
        const timer = setInterval(() => {
          start += step;
          if (start >= end) { setVal(end); clearInterval(timer); }
          else setVal(Math.floor(start));
        }, 16);
        observer.disconnect();
      }
    }, { threshold: 0.3 });
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, [end, duration]);
  return <span ref={ref}>{val.toLocaleString()}{suffix}</span>;
};

/* ─── scroll reveal ─── */
const Reveal = ({ children, delay = 0, style = {} }) => {
  const [visible, setVisible] = useState(false);
  const ref = useRef(null);
  useEffect(() => {
    const observer = new IntersectionObserver(([e]) => {
      if (e.isIntersecting) { setVisible(true); observer.disconnect(); }
    }, { threshold: 0.15 });
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, []);
  return (
    <div ref={ref} style={{ ...style, opacity: visible ? 1 : 0, transform: visible ? 'translateY(0)' : 'translateY(40px)', transition: `opacity 0.7s ease ${delay}s, transform 0.7s ease ${delay}s` }}>
      {children}
    </div>
  );
};

/* ─── FAQ item ─── */
const FAQItem = ({ q, a, borderColor, textColor, mutedColor, accentColor }) => {
  const [open, setOpen] = useState(false);
  return (
    <div style={{ borderBottom: `1px solid ${borderColor}`, padding: '24px 0', cursor: 'pointer' }} onClick={() => setOpen(!open)}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <span style={{ fontSize: 18, fontWeight: 600, color: textColor, fontFamily: 'Plus Jakarta Sans, sans-serif' }}>{q}</span>
        <span style={{ fontSize: 24, color: accentColor, fontWeight: 300, transition: 'transform 0.3s', transform: open ? 'rotate(45deg)' : 'none' }}>+</span>
      </div>
      <div style={{ maxHeight: open ? 200 : 0, overflow: 'hidden', transition: 'max-height 0.4s ease' }}>
        <p style={{ marginTop: 16, fontSize: 15, lineHeight: 1.7, color: mutedColor, fontFamily: 'Plus Jakarta Sans, sans-serif' }}>{a}</p>
      </div>
    </div>
  );
};

/* ─── MAIN LANDING PAGE ─── */
const LandingPage = () => {
  const { isDark, toggleTheme } = useTheme();
  const { user } = useAuth();
  const [scrolled, setScrolled] = useState(false);
  const [mobileNav, setMobileNav] = useState(false);
  const [demoForm, setDemoForm] = useState({ firstName: '', lastName: '', email: '', organization: '', orgType: '', message: '', website: '' });
  const [demoSubmitting, setDemoSubmitting] = useState(false);
  const [demoSubmitted, setDemoSubmitted] = useState(false);
  const [demoError, setDemoError] = useState('');
  const [demoTab, setDemoTab] = useState('Dashboard');
  // Server-signed, single-use question (same as signup), so bots can't bypass it by calling the API
  const [captcha, setCaptcha] = useState({ question: '', token: '' });
  const refreshCaptcha = () => {
    setCaptchaAnswer('');
    fetch(`${process.env.REACT_APP_API_URL || '/api'}/auth/captcha`).then(r => r.json()).then(setCaptcha).catch(() => setCaptcha({ question: '', token: '' }));
  };
  useEffect(() => { refreshCaptcha(); }, []);
  const [captchaAnswer, setCaptchaAnswer] = useState('');

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 60);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollTo = (id) => {
    setMobileNav(false);
    document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' });
  };

  const handleDemoSubmit = async (e) => {
    e.preventDefault();
    setDemoError('');
    if (!demoForm.firstName || !demoForm.email) {
      setDemoError('Please fill in at least your name and email.');
      return;
    }
    if (!captchaAnswer.trim()) {
      setDemoError('Please answer the quick verification question.');
      return;
    }
    setDemoSubmitting(true);
    try {
      const API_BASE = process.env.REACT_APP_API_URL || '/api';
      const res = await fetch(`${API_BASE}/contact/demo-request`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...demoForm, captchaToken: captcha.token, captchaAnswer })
      });
      if (res.ok) {
        setDemoSubmitted(true);
      } else {
        const data = await res.json();
        setDemoError(data.message || 'Something went wrong. Please try again.');
        refreshCaptcha();
      }
    } catch {
      setDemoSubmitted(true);
    }
    setDemoSubmitting(false);
  };

  /* ─── THEME COLORS ─── */
  const c = {
    pageBg:       isDark ? '#020617' : '#ffffff',
    pageBg2:      isDark ? '#0a0f1e' : '#f8fafc',
    cardBg:       isDark ? 'linear-gradient(135deg, #0f172a 0%, #1e293b 100%)' : 'linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)',
    cardBgSolid:  isDark ? '#0f172a' : '#ffffff',
    cardBorder:   isDark ? '#1e293b' : '#e2e8f0',
    textPrimary:  isDark ? '#f1f5f9' : '#0f172a',
    textSecondary:isDark ? '#94a3b8' : '#64748b',
    textMuted:    isDark ? '#64748b' : '#94a3b8',
    accent:       '#00d4aa',
    accentDark:   '#00b894',
    navBg:        isDark ? 'rgba(2,6,23,0.92)' : 'rgba(255,255,255,0.95)',
    navBorder:    isDark ? 'rgba(30,41,59,0.6)' : 'rgba(226,232,240,0.8)',
    navLink:      isDark ? '#94a3b8' : '#475569',
    navShadow:    isDark ? 'none' : '0 1px 3px rgba(0,0,0,0.06)',
    pillBg:       isDark ? 'rgba(15,23,42,0.6)' : 'rgba(241,245,249,0.8)',
    pillBorder:   isDark ? '#1e293b' : '#e2e8f0',
    pillText:     isDark ? '#94a3b8' : '#475569',
    dotGrid:      isDark ? 'rgba(0,212,170,0.12)' : 'rgba(0,212,170,0.06)',
    orbGreen:     isDark ? 'rgba(0,212,170,0.08)' : 'rgba(0,212,170,0.04)',
    orbBlue:      isDark ? 'rgba(56,189,248,0.06)' : 'rgba(56,189,248,0.03)',
    floatCardBg:  isDark ? 'rgba(15,23,42,0.7)' : 'rgba(255,255,255,0.9)',
    floatCardBorder: isDark ? '#1e293b' : '#e2e8f0',
    statsBg:      isDark ? 'linear-gradient(180deg, #020617 0%, #0a0f1e 100%)' : 'linear-gradient(180deg, #f8fafc 0%, #f1f5f9 100%)',
    statsBorder:  isDark ? '#0f172a' : '#e2e8f0',
    stepNum:      isDark ? 'rgba(0,212,170,0.1)' : 'rgba(0,212,170,0.08)',
    stepBg:       isDark ? 'linear-gradient(180deg, rgba(15,23,42,0.8) 0%, rgba(15,23,42,0.4) 100%)' : 'linear-gradient(180deg, #ffffff 0%, #f8fafc 100%)',
    inputBg:      isDark ? '#0f172a' : '#ffffff',
    inputBorder:  isDark ? '#1e293b' : '#d1d5db',
    inputText:    isDark ? '#f1f5f9' : '#0f172a',
    inputPlaceholder: isDark ? '#475569' : '#9ca3af',
    selectText:   isDark ? '#94a3b8' : '#6b7280',
    sectionLabel: '#00d4aa',
    wsCardBg:     isDark ? 'rgba(15,23,42,0.8)' : 'rgba(255,255,255,0.9)',
    footerBg:     isDark ? '#020617' : '#0f172a',
    footerBorder: isDark ? '#0f172a' : '#1e293b',
    // CTA buttons
    btnSecBorder: isDark ? '#334155' : '#d1d5db',
    btnSecText:   isDark ? '#f1f5f9' : '#0f172a',
  };

  const inputStyle = { padding: '14px 18px', borderRadius: 12, border: `1px solid ${c.inputBorder}`, background: c.inputBg, color: c.inputText, fontSize: 14, outline: 'none', fontFamily: 'Plus Jakarta Sans, sans-serif', boxSizing: 'border-box', transition: 'border-color 0.3s, background 0.3s' };

  const modules = [
    { icon: '🔬', title: 'Disease Surveillance', desc: 'Real-time case tracking, outbreak detection, and automated alert systems with AI-powered anomaly detection across all jurisdictions.' },
    { icon: '💉', title: 'Vaccination Tracking', desc: 'Monitor immunization coverage rates, identify under-vaccinated populations, and track campaign progress across regions.' },
    { icon: '📊', title: 'Vital Statistics', desc: 'Comprehensive birth and death registration with trend analysis, mortality patterns, and demographic breakdowns.' },
    { icon: '🏘️', title: 'SDOH Tracker', desc: 'Map social determinants — housing, food security, education, employment — to understand root causes of health disparities.' },
    { icon: '⚖️', title: 'Health Equity', desc: 'Identify and address health disparities across race, gender, income, and geography with equity-focused analytics.' },
    { icon: '🤖', title: 'AI Insights', desc: 'Natural language queries, automated anomaly detection, predictive modeling, and AI-generated reports for faster decisions.' },
    { icon: '📋', title: 'Assessments', desc: 'Build custom health assessment templates with automated scoring, risk stratification, and AI-powered analysis.' },
    { icon: '🏥', title: 'Health Programs', desc: 'Plan, execute, and evaluate community health interventions with budget tracking and outcome measurement.' },
    { icon: '📈', title: 'Reports & Analytics', desc: 'AI-generated reports with executive summaries, trend analysis, and actionable recommendations.' },
    { icon: '👥', title: 'User Management', desc: 'Role-based access control with five levels — Super Admin, Admin, Analyst, Field Worker, and Viewer.' },
    { icon: '🌍', title: 'Multi-Tenant Workspaces', desc: 'Isolated workspaces for states, counties, and countries. Each with its own admin, users, data, and full feature set.' },
    { icon: '📤', title: 'CSV Bulk Upload', desc: 'Import thousands of records instantly via CSV templates. Drag-and-drop with validation and error reporting.' },
  ];

  const steps = [
    { num: '01', title: 'Create Your Workspace', desc: 'Set up an isolated environment for your jurisdiction — state, county, city, or country — in under 2 minutes.' },
    { num: '02', title: 'Onboard Your Team', desc: 'Add administrators, analysts, and field workers with role-based permissions tailored to your workflow.' },
    { num: '03', title: 'Import or Collect Data', desc: 'Bulk import existing data via CSV or start collecting through our intuitive forms and mobile-ready interface.' },
    { num: '04', title: 'Monitor & Respond', desc: 'Real-time dashboards, automated outbreak alerts, and AI-powered insights help you act before crises escalate.' },
  ];

  const faqs = [
    { q: 'What types of organizations can use EpiSurveillance?', a: 'State health departments, county public health agencies, national surveillance programs, international health organizations, hospital systems, and academic research institutions. Our multi-tenant workspace model adapts to any jurisdiction level.' },
    { q: 'How does workspace isolation work?', a: 'Each workspace operates as a completely independent system. Data, users, reports, and settings are fully isolated. A Virginia workspace admin cannot see Florida data and vice versa. Super administrators can oversee all workspaces from a unified console.' },
    { q: 'Can we import our existing surveillance data?', a: 'Yes. Every module supports CSV bulk import with downloadable templates and row-level validation. Our import tool handles thousands of records at once with detailed error reporting for any issues.' },
    { q: 'Is the platform HIPAA compliant?', a: 'EpiSurveillance is designed with security-first architecture including encrypted data at rest and in transit, role-based access control, comprehensive audit logging, and session management. The platform is built to support HIPAA compliance.' },
    { q: 'How does the AI-powered analytics work?', a: 'Our AI engine analyzes disease patterns, detects statistical anomalies, generates natural language insights, and produces automated reports. It identifies emerging outbreaks before they become crises and provides evidence-based recommendations.' },
    { q: 'What kind of support do you offer?', a: 'We provide dedicated onboarding assistance, 24/7 technical support, custom training for your team, and a dedicated account manager for enterprise clients. Our team includes epidemiologists who understand your workflow.' },
  ];

  return (
    <div style={{ fontFamily: "'Plus Jakarta Sans', sans-serif", background: c.pageBg, color: c.textPrimary, overflowX: 'hidden', transition: 'background 0.4s, color 0.3s' }}>
      <style>{`
        * { margin: 0; padding: 0; box-sizing: border-box; }
        html { scroll-behavior: smooth; }
        @keyframes pulse-grid { 0%, 100% { opacity: 0.03; } 50% { opacity: 0.08; } }
        @keyframes float { 0%, 100% { transform: translateY(0); } 50% { transform: translateY(-12px); } }
        @keyframes fadeInUp { from { opacity: 0; transform: translateY(30px); } to { opacity: 1; transform: translateY(0); } }
        @keyframes shimmer { 0% { background-position: -200% center; } 100% { background-position: 200% center; } }
        .nav-link { color: ${c.navLink}; text-decoration: none; font-size: 14px; font-weight: 500; transition: color 0.2s; cursor: pointer; }
        .nav-link:hover { color: #00d4aa; }
        .hero-grid { position: absolute; inset: 0; background-image: radial-gradient(circle, ${c.dotGrid} 1px, transparent 1px); background-size: 40px 40px; animation: pulse-grid 4s ease-in-out infinite; }
        .module-card { background: ${c.cardBg}; border: 1px solid ${c.cardBorder}; border-radius: 20px; padding: 32px; transition: all 0.4s cubic-bezier(0.4, 0, 0.2, 1); cursor: default; }
        .module-card:hover { border-color: #00d4aa; transform: translateY(-4px); box-shadow: 0 20px 60px rgba(0,212,170,0.1); }
        .step-card { position: relative; padding: 40px; background: ${c.stepBg}; border: 1px solid ${c.cardBorder}; border-radius: 24px; transition: all 0.4s; }
        .step-card:hover { border-color: rgba(0,212,170,0.4); }
        .cta-btn { display: inline-flex; align-items: center; gap: 8px; padding: 16px 36px; border-radius: 12px; font-size: 16px; font-weight: 700; text-decoration: none; transition: all 0.3s; cursor: pointer; border: none; font-family: 'Plus Jakarta Sans', sans-serif; }
        .cta-primary { background: linear-gradient(135deg, #00d4aa 0%, #00b894 100%); color: #020617; }
        .cta-primary:hover { transform: translateY(-2px); box-shadow: 0 12px 40px rgba(0,212,170,0.35); }
        .cta-secondary { background: transparent; color: ${c.btnSecText}; border: 2px solid ${c.btnSecBorder}; }
        .cta-secondary:hover { border-color: #00d4aa; color: #00d4aa; }
        .gradient-text { background: linear-gradient(135deg, #00d4aa 0%, #38bdf8 50%, #818cf8 100%); -webkit-background-clip: text; -webkit-text-fill-color: transparent; background-clip: text; }
        .shimmer-text { background: linear-gradient(90deg, #00d4aa, #38bdf8, #00d4aa); background-size: 200% auto; -webkit-background-clip: text; -webkit-text-fill-color: transparent; background-clip: text; animation: shimmer 3s linear infinite; }
        .theme-toggle { background: ${isDark ? 'rgba(250,204,21,0.1)' : 'rgba(99,102,241,0.08)'}; border: 1px solid ${isDark ? 'rgba(250,204,21,0.2)' : 'rgba(99,102,241,0.15)'}; color: ${isDark ? '#facc15' : '#6366f1'}; cursor: pointer; padding: 8px 10px; border-radius: 10px; font-size: 18px; transition: all 0.3s; display: flex; align-items: center; }
        .theme-toggle:hover { transform: scale(1.1); }
        .mobile-hamburger { display: none; background: none; border: none; cursor: pointer; padding: 8px; color: ${c.textPrimary}; font-size: 24px; z-index: 1002; }
        .mobile-overlay { display: none; position: fixed; inset: 0; background: rgba(0,0,0,0.6); z-index: 998; }
        .mobile-overlay.open { display: block; }
        .mobile-drawer { position: fixed; top: 0; right: -300px; width: 280px; height: 100vh; background: ${isDark ? '#0f172a' : '#ffffff'}; z-index: 999; transition: right 0.3s ease; padding: 80px 24px 24px; display: flex; flex-direction: column; gap: 8px; box-shadow: -4px 0 20px rgba(0,0,0,0.2); overflow-y: auto; }
        .mobile-drawer.open { right: 0; }
        .mobile-drawer a, .mobile-drawer span { display: block; padding: 14px 16px; border-radius: 10px; font-size: 16px; font-weight: 500; color: ${c.textPrimary}; text-decoration: none; cursor: pointer; transition: background 0.2s; }
        .mobile-drawer a:hover, .mobile-drawer span:hover { background: ${isDark ? 'rgba(0,212,170,0.08)' : 'rgba(0,212,170,0.06)'}; color: #00d4aa; }
        .mobile-close { position: absolute; top: 20px; right: 20px; background: none; border: none; font-size: 28px; cursor: pointer; color: ${c.textSecondary}; }
        .demo-tab-btn { padding: 10px 20px; border-radius: 8px; border: 1px solid transparent; font-size: 14px; font-weight: 600; cursor: pointer; transition: all 0.2s; font-family: 'Plus Jakarta Sans', sans-serif; white-space: nowrap; }
        .demo-tab-btn.active { border-color: #00d4aa; color: #00d4aa; background: rgba(0,212,170,0.08); }
        .demo-tab-btn:not(.active) { color: #64748b; background: transparent; }
        .demo-tab-btn:not(.active):hover { color: #00d4aa; border-color: rgba(0,212,170,0.3); }
        .demo-table-row:hover { background: rgba(0,212,170,0.03); }
        .demo-bar { border-radius: 4px 4px 0 0; background: linear-gradient(180deg, #00d4aa 0%, #00b894 100%); transition: opacity 0.3s; }
        .demo-bar:hover { opacity: 0.8; }
        @media (max-width: 768px) {
          .desktop-nav { display: none !important; }
          .mobile-hamburger { display: flex !important; align-items: center; justify-content: center; }
          .hero-title { font-size: 36px !important; }
          .section-title { font-size: 32px !important; }
          .modules-grid { grid-template-columns: 1fr !important; }
          .steps-grid { grid-template-columns: 1fr !important; }
          .stats-grid { grid-template-columns: 1fr 1fr !important; }
          .footer-grid { grid-template-columns: 1fr !important; }
          .hero-buttons { flex-direction: column !important; }
          .contact-grid { grid-template-columns: 1fr !important; }
          .hero-float { display: none !important; }
          .workspace-grid { grid-template-columns: 1fr !important; }
          nav { padding: 0 16px !important; }
          section { padding-left: 20px !important; padding-right: 20px !important; }
          footer { padding-left: 20px !important; padding-right: 20px !important; }
        }
      `}</style>

      {/* ═══ NAVIGATION ═══ */}
      <nav style={{
        position: 'fixed', top: 0, left: 0, right: 0, zIndex: 1000,
        padding: '0 48px', height: 72,
        background: scrolled ? c.navBg : 'transparent',
        backdropFilter: scrolled ? 'blur(20px)' : 'none',
        borderBottom: scrolled ? `1px solid ${c.navBorder}` : 'none',
        boxShadow: scrolled ? c.navShadow : 'none',
        transition: 'all 0.3s', display: 'flex', alignItems: 'center', justifyContent: 'space-between'
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <div style={{ width: 42, height: 42, borderRadius: 12, background: 'linear-gradient(135deg, #00d4aa, #00b894)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 800, fontSize: 16, color: '#020617', fontFamily: 'DM Serif Display, serif' }}>ES</div>
          <div>
            <div style={{ fontWeight: 800, fontSize: 18, letterSpacing: '-0.5px', color: c.textPrimary }}>EpiSurveillance</div>
            <div style={{ fontSize: 10, fontWeight: 600, letterSpacing: '2px', color: '#00d4aa', textTransform: 'uppercase' }}>Modern Disease Surveillance</div>
          </div>
        </div>
        <div className="desktop-nav" style={{ display: 'flex', alignItems: 'center', gap: 28 }}>
          {['Features', 'How It Works', 'Modules', 'FAQ', 'Contact'].map(item => (
            <span key={item} className="nav-link" onClick={() => scrollTo(item.toLowerCase().replace(/ /g, '-'))}>{item}</span>
          ))}
          <Link to="/pricing" className="nav-link" style={{ textDecoration: 'none' }}>Pricing</Link>
          <Link to="/blog" className="nav-link" style={{ textDecoration: 'none' }}>Blog</Link>
          <button className="theme-toggle" onClick={toggleTheme} title={isDark ? 'Light Mode' : 'Dark Mode'}>
            {isDark ? '☀️' : '🌙'}
          </button>
          {user ? (
            <Link to="/dashboard" className="cta-btn cta-primary" style={{ padding: '10px 24px', fontSize: 14 }}>Dashboard →</Link>
          ) : (<>
            <Link to="/login" className="cta-btn cta-secondary" style={{ padding: '10px 24px', fontSize: 14 }}>Sign In</Link>
            <Link to="/register" className="cta-btn cta-primary" style={{ padding: '10px 24px', fontSize: 14 }}>Get Started</Link>
          </>)}
        </div>
        {/* Hamburger */}
        <button className="mobile-hamburger" onClick={() => setMobileNav(true)}>☰</button>
      </nav>

      {/* Mobile Drawer */}
      <div className={`mobile-overlay ${mobileNav ? 'open' : ''}`} onClick={() => setMobileNav(false)} />
      <div className={`mobile-drawer ${mobileNav ? 'open' : ''}`}>
        <button className="mobile-close" onClick={() => setMobileNav(false)}>✕</button>
        {['Features', 'How It Works', 'Modules', 'FAQ', 'Contact'].map(item => (
          <span key={item} onClick={() => { scrollTo(item.toLowerCase().replace(/ /g, '-')); setMobileNav(false); }}>{item}</span>
        ))}
        <Link to="/pricing" onClick={() => setMobileNav(false)} style={{ textDecoration: 'none', color: '#00d4aa', fontWeight: 600 }}>Pricing</Link>
        <Link to="/blog" onClick={() => setMobileNav(false)} style={{ textDecoration: 'none', color: '#94a3b8', fontWeight: 500 }}>Blog</Link>
        <div style={{ borderTop: `1px solid ${c.cardBorder}`, margin: '8px 0' }} />
        <span onClick={() => { toggleTheme(); }}>{isDark ? '☀️ Light Mode' : '🌙 Dark Mode'}</span>
        {user ? (
          <Link to="/dashboard" style={{ background: 'linear-gradient(135deg, #00d4aa, #00b894)', color: '#020617', fontWeight: 700, textAlign: 'center', borderRadius: 10 }} onClick={() => setMobileNav(false)}>Dashboard →</Link>
        ) : (<>
          <Link to="/login" onClick={() => setMobileNav(false)}>Sign In</Link>
          <Link to="/register" style={{ background: 'linear-gradient(135deg, #00d4aa, #00b894)', color: '#020617', fontWeight: 700, textAlign: 'center', borderRadius: 10 }} onClick={() => setMobileNav(false)}>Get Started</Link>
        </>)}
      </div>

      {/* ═══ HERO ═══ */}
      <section style={{ position: 'relative', minHeight: '100vh', display: 'flex', alignItems: 'center', padding: '120px 48px 80px', overflow: 'hidden' }}>
        <div className="hero-grid" />
        <div style={{ position: 'absolute', top: '10%', right: '10%', width: 500, height: 500, borderRadius: '50%', background: `radial-gradient(circle, ${c.orbGreen} 0%, transparent 70%)`, filter: 'blur(60px)', animation: 'float 8s ease-in-out infinite' }} />
        <div style={{ position: 'absolute', bottom: '20%', left: '5%', width: 400, height: 400, borderRadius: '50%', background: `radial-gradient(circle, ${c.orbBlue} 0%, transparent 70%)`, filter: 'blur(60px)', animation: 'float 6s ease-in-out infinite 1s' }} />

        <div style={{ maxWidth: 1280, margin: '0 auto', width: '100%', position: 'relative', zIndex: 2 }}>
          <div style={{ maxWidth: 820 }}>
            <div style={{ display: 'inline-block', padding: '8px 20px', borderRadius: 50, border: `1px solid ${c.pillBorder}`, background: c.pillBg, marginBottom: 32, animation: 'fadeInUp 0.6s ease' }}>
              <span style={{ fontSize: 13, fontWeight: 600, color: c.pillText }}>🚀 The Future of Public Health is Here</span>
            </div>
            <h1 className="hero-title" style={{ fontFamily: "'DM Serif Display', serif", fontSize: 72, lineHeight: 1.05, fontWeight: 400, letterSpacing: '-2px', marginBottom: 28, animation: 'fadeInUp 0.6s ease 0.1s both', color: c.textPrimary }}>
              Surveillance That<br />
              <span className="gradient-text">Saves Lives.</span>
            </h1>
            <p style={{ fontSize: 20, lineHeight: 1.7, color: c.textSecondary, maxWidth: 600, marginBottom: 48, animation: 'fadeInUp 0.6s ease 0.2s both' }}>
              The most comprehensive disease surveillance platform for modern public health. Real-time tracking, AI-powered insights, and multi-jurisdiction workspaces — designed for the teams protecting our communities.
            </p>
            <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap', animation: 'fadeInUp 0.6s ease 0.3s both' }} className="hero-buttons">
              <Link to="/register" className="cta-btn cta-primary" style={{ fontSize: 17 }}>
                Start Free Trial <span style={{ fontSize: 20 }}>→</span>
              </Link>
              <button className="cta-btn cta-secondary" onClick={() => scrollTo('how-it-works')} style={{ fontSize: 17 }}>
                See How It Works
              </button>
            </div>
            <button onClick={() => scrollTo('contact')} style={{ marginTop: 28, display: 'inline-flex', alignItems: 'center', gap: 10, padding: '10px 18px', borderRadius: 12, border: `1px solid ${c.pillBorder}`, background: c.pillBg, color: c.textSecondary, fontSize: 15, cursor: 'pointer', textAlign: 'left', animation: 'fadeInUp 0.6s ease 0.4s both' }}>
              <span style={{ fontSize: 18 }}>⚡</span>
              <span>Need something specific? <strong style={{ color: c.pillText }}>We build custom surveillance to your requirements in about 2 weeks.</strong> Talk to us →</span>
            </button>
          </div>

          <div className="hero-float" style={{ position: 'absolute', right: 0, top: '50%', transform: 'translateY(-50%)', display: 'flex', flexDirection: 'column', gap: 20 }}>
            {[['15+', 'Modules'], ['Real-Time', 'Alerts'], ['AI', 'Powered'], ['Multi', 'Tenant']].map(([v, l], i) => (
              <div key={l} style={{ padding: '16px 24px', background: c.floatCardBg, border: `1px solid ${c.floatCardBorder}`, borderRadius: 16, backdropFilter: 'blur(10px)', textAlign: 'center', animation: `fadeInUp 0.6s ease ${0.4 + i * 0.1}s both`, minWidth: 120 }}>
                <div style={{ fontSize: 24, fontWeight: 800, color: '#00d4aa', fontFamily: "'DM Serif Display', serif" }}>{v}</div>
                <div style={{ fontSize: 11, fontWeight: 600, color: c.textMuted, textTransform: 'uppercase', letterSpacing: '1px', marginTop: 4 }}>{l}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ═══ DEMO PREVIEW ═══ */}
      <section style={{ padding: '100px 48px', background: c.pageBg2 }}>
        <div style={{ maxWidth: 1280, margin: '0 auto' }}>
          <Reveal>
            <div style={{ textAlign: 'center', marginBottom: 56 }}>
              <span style={{ fontSize: 13, fontWeight: 700, letterSpacing: '3px', textTransform: 'uppercase', color: c.sectionLabel }}>Live Preview</span>
              <h2 className="section-title" style={{ fontFamily: "'DM Serif Display', serif", fontSize: 52, fontWeight: 400, marginTop: 16, letterSpacing: '-1px', color: c.textPrimary }}>
                See EpiSurveillance in Action
              </h2>
              <p style={{ fontSize: 18, color: c.textMuted, maxWidth: 560, margin: '16px auto 0', lineHeight: 1.7 }}>
                Explore the platform without signing up
              </p>
            </div>
          </Reveal>

          <Reveal delay={0.1}>
            {/* Browser chrome wrapper */}
            <div style={{ borderRadius: 20, overflow: 'hidden', border: `1px solid ${c.cardBorder}`, boxShadow: isDark ? '0 32px 80px rgba(0,0,0,0.5)' : '0 32px 80px rgba(0,0,0,0.12)', background: c.cardBgSolid }}>

              {/* Browser chrome header */}
              <div style={{ background: isDark ? '#1e293b' : '#f1f5f9', borderBottom: `1px solid ${c.cardBorder}`, padding: '12px 20px', display: 'flex', alignItems: 'center', gap: 16 }}>
                <div style={{ display: 'flex', gap: 7 }}>
                  <div style={{ width: 13, height: 13, borderRadius: '50%', background: '#ef4444' }} />
                  <div style={{ width: 13, height: 13, borderRadius: '50%', background: '#f59e0b' }} />
                  <div style={{ width: 13, height: 13, borderRadius: '50%', background: '#22c55e' }} />
                </div>
                <div style={{ flex: 1, background: isDark ? '#0f172a' : '#ffffff', borderRadius: 8, border: `1px solid ${c.cardBorder}`, padding: '5px 14px', fontSize: 13, color: c.textMuted, fontFamily: 'monospace', maxWidth: 340, margin: '0 auto' }}>
                  episurveillance.com/dashboard
                </div>
              </div>

              {/* Tab navigation */}
              <div style={{ borderBottom: `1px solid ${c.cardBorder}`, padding: '0 24px', background: c.cardBgSolid, display: 'flex', gap: 8, overflowX: 'auto' }}>
                {['Dashboard', 'Surveillance', 'Vaccinations', 'Health Equity', 'AI Insights'].map(tab => (
                  <button
                    key={tab}
                    className={`demo-tab-btn${demoTab === tab ? ' active' : ''}`}
                    onClick={() => setDemoTab(tab)}
                    style={{ marginBottom: -1, borderBottomLeftRadius: 0, borderBottomRightRadius: 0, borderBottom: demoTab === tab ? '2px solid #00d4aa' : '2px solid transparent' }}
                  >
                    {tab}
                  </button>
                ))}
              </div>

              {/* Tab content panel */}
              <div style={{ padding: '28px 28px 24px', minHeight: 480, background: c.cardBgSolid }}>

                {/* ── DASHBOARD TAB ── */}
                {demoTab === 'Dashboard' && (
                  <div>
                    <h3 style={{ fontSize: 20, fontWeight: 700, color: c.textPrimary, marginBottom: 20 }}>Population Health Dashboard</h3>
                    {/* Stat cards */}
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 16, marginBottom: 24 }}>
                      {[
                        { label: 'Total Cases', value: '2,847', sub: '+12% this month', color: '#3b82f6' },
                        { label: 'Active Alerts', value: '23', sub: 'Critical', color: '#ef4444' },
                        { label: 'Vaccination Rate', value: '65.2%', sub: 'National avg', color: '#00d4aa' },
                        { label: 'Mortality Rate', value: '4.2', sub: 'per 1000', color: '#f59e0b' },
                      ].map(s => (
                        <div key={s.label} style={{ padding: '16px 18px', borderRadius: 12, border: `1px solid ${c.cardBorder}`, background: isDark ? '#0a0f1e' : '#f8fafc' }}>
                          <div style={{ fontSize: 12, fontWeight: 600, color: c.textMuted, textTransform: 'uppercase', letterSpacing: '1px', marginBottom: 6 }}>{s.label}</div>
                          <div style={{ fontSize: 26, fontWeight: 800, color: s.color, fontFamily: "'DM Serif Display', serif" }}>{s.value}</div>
                          <div style={{ fontSize: 12, color: c.textMuted, marginTop: 4 }}>{s.sub}</div>
                        </div>
                      ))}
                    </div>
                    {/* Bar chart */}
                    <div style={{ padding: '20px', borderRadius: 12, border: `1px solid ${c.cardBorder}`, background: isDark ? '#0a0f1e' : '#f8fafc', marginBottom: 24 }}>
                      <div style={{ fontSize: 13, fontWeight: 700, color: c.textSecondary, marginBottom: 16, textTransform: 'uppercase', letterSpacing: '1px' }}>Disease Trend (Monthly)</div>
                      <div style={{ display: 'flex', alignItems: 'flex-end', gap: 8, height: 80 }}>
                        {[60, 75, 55, 80, 70, 90, 65, 85, 72, 88, 76, 82].map((h, i) => (
                          <div key={i} style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4, height: '100%', justifyContent: 'flex-end' }}>
                            <div className="demo-bar" style={{ width: '100%', height: `${h}%` }} />
                          </div>
                        ))}
                      </div>
                      <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 8 }}>
                        {['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'].map(m => (
                          <div key={m} style={{ flex: 1, textAlign: 'center', fontSize: 10, color: c.textMuted }}>{m}</div>
                        ))}
                      </div>
                    </div>
                    {/* Alerts */}
                    <div style={{ padding: '16px 20px', borderRadius: 12, border: `1px solid ${c.cardBorder}`, background: isDark ? '#0a0f1e' : '#f8fafc' }}>
                      <div style={{ fontSize: 13, fontWeight: 700, color: c.textSecondary, marginBottom: 14, textTransform: 'uppercase', letterSpacing: '1px' }}>Recent Outbreak Alerts</div>
                      {[
                        { name: 'Heart Disease — Northeast', badge: 'mild', bg: 'rgba(34,197,94,0.1)', col: '#22c55e' },
                        { name: 'Tuberculosis — Southeast', badge: 'critical', bg: 'rgba(239,68,68,0.1)', col: '#ef4444' },
                        { name: 'Measles — West Coast', badge: 'severe', bg: 'rgba(249,115,22,0.1)', col: '#f97316' },
                      ].map(a => (
                        <div key={a.name} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '10px 0', borderBottom: `1px solid ${c.cardBorder}` }}>
                          <span style={{ fontSize: 14, color: c.textPrimary }}>{a.name}</span>
                          <span style={{ padding: '3px 10px', borderRadius: 20, fontSize: 11, fontWeight: 700, background: a.bg, color: a.col, textTransform: 'uppercase', letterSpacing: '0.5px' }}>{a.badge}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* ── SURVEILLANCE TAB ── */}
                {demoTab === 'Surveillance' && (
                  <div>
                    <h3 style={{ fontSize: 20, fontWeight: 700, color: c.textPrimary, marginBottom: 16 }}>Disease Surveillance</h3>
                    <div style={{ display: 'flex', gap: 12, marginBottom: 20, flexWrap: 'wrap' }}>
                      {[['200', 'Total Cases'], ['14', 'Pages of Records'], ['8', 'Active Alerts']].map(([v, l]) => (
                        <div key={l} style={{ padding: '8px 18px', borderRadius: 20, border: `1px solid ${c.cardBorder}`, background: isDark ? '#0a0f1e' : '#f8fafc', fontSize: 13 }}>
                          <span style={{ fontWeight: 800, color: '#00d4aa', marginRight: 6 }}>{v}</span>
                          <span style={{ color: c.textMuted }}>{l}</span>
                        </div>
                      ))}
                    </div>
                    <div style={{ borderRadius: 12, border: `1px solid ${c.cardBorder}`, overflow: 'hidden' }}>
                      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                        <thead>
                          <tr style={{ background: isDark ? '#0a0f1e' : '#f8fafc' }}>
                            {['DISEASE', 'STATUS', 'SEVERITY', 'REGION', 'AGE/GENDER'].map(h => (
                              <th key={h} style={{ padding: '10px 16px', textAlign: 'left', fontSize: 11, fontWeight: 700, color: c.textMuted, textTransform: 'uppercase', letterSpacing: '1px', borderBottom: `1px solid ${c.cardBorder}` }}>{h}</th>
                            ))}
                          </tr>
                        </thead>
                        <tbody>
                          {[
                            ['COVID-19', 'confirmed', '#22c55e', 'rgba(34,197,94,0.1)', 'mild', '#22c55e', 'rgba(34,197,94,0.1)', 'Northeast', '42 / male'],
                            ['Tuberculosis', 'suspected', '#eab308', 'rgba(234,179,8,0.1)', 'critical', '#ef4444', 'rgba(239,68,68,0.1)', 'Southeast', '74 / female'],
                            ['Heart Disease', 'confirmed', '#22c55e', 'rgba(34,197,94,0.1)', 'moderate', '#eab308', 'rgba(234,179,8,0.1)', 'Midwest', '58 / female'],
                            ['Influenza A', 'under_investigation', '#3b82f6', 'rgba(59,130,246,0.1)', 'severe', '#ef4444', 'rgba(239,68,68,0.1)', 'Pacific Northwest', '8 / female'],
                            ['Hypertension', 'resolved', '#64748b', 'rgba(100,116,139,0.1)', 'mild', '#22c55e', 'rgba(34,197,94,0.1)', 'Southwest', '78 / female'],
                            ['Measles', 'confirmed', '#22c55e', 'rgba(34,197,94,0.1)', 'critical', '#ef4444', 'rgba(239,68,68,0.1)', 'West Coast', '46 / female'],
                          ].map(([disease, status, sCol, sBg, sev, vCol, vBg, region, age]) => (
                            <tr key={disease} className="demo-table-row" style={{ borderBottom: `1px solid ${c.cardBorder}` }}>
                              <td style={{ padding: '11px 16px', fontWeight: 600, color: c.textPrimary }}>{disease}</td>
                              <td style={{ padding: '11px 16px' }}><span style={{ padding: '3px 10px', borderRadius: 20, fontSize: 11, fontWeight: 700, background: sBg, color: sCol }}>{status}</span></td>
                              <td style={{ padding: '11px 16px' }}><span style={{ padding: '3px 10px', borderRadius: 20, fontSize: 11, fontWeight: 700, background: vBg, color: vCol }}>{sev}</span></td>
                              <td style={{ padding: '11px 16px', color: c.textSecondary }}>{region}</td>
                              <td style={{ padding: '11px 16px', color: c.textMuted }}>{age}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}

                {/* ── VACCINATIONS TAB ── */}
                {demoTab === 'Vaccinations' && (
                  <div>
                    <h3 style={{ fontSize: 20, fontWeight: 700, color: c.textPrimary, marginBottom: 20 }}>Vaccination &amp; Immunization Tracker</h3>
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 14, marginBottom: 20 }}>
                      {[
                        { label: 'Total Doses', value: '1,316,574', color: '#3b82f6' },
                        { label: 'Avg Coverage', value: '65.2%', color: '#00d4aa' },
                        { label: 'Vaccine Types', value: '7', color: '#8b5cf6' },
                        { label: 'Under-vaccinated Groups', value: '8', color: '#f59e0b' },
                      ].map(s => (
                        <div key={s.label} style={{ padding: '14px 16px', borderRadius: 12, border: `1px solid ${c.cardBorder}`, background: isDark ? '#0a0f1e' : '#f8fafc' }}>
                          <div style={{ fontSize: 11, fontWeight: 600, color: c.textMuted, textTransform: 'uppercase', letterSpacing: '1px', marginBottom: 6 }}>{s.label}</div>
                          <div style={{ fontSize: 22, fontWeight: 800, color: s.color, fontFamily: "'DM Serif Display', serif" }}>{s.value}</div>
                        </div>
                      ))}
                    </div>
                    {/* Under-vaccinated warning */}
                    <div style={{ padding: '14px 18px', borderRadius: 12, border: `1px solid rgba(239,68,68,0.3)`, background: 'rgba(239,68,68,0.05)', marginBottom: 20 }}>
                      <div style={{ fontSize: 12, fontWeight: 700, color: '#ef4444', textTransform: 'uppercase', letterSpacing: '1px', marginBottom: 10 }}>Under-vaccinated Warning Zones</div>
                      <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
                        {[['HPV — Northeast', '40%'], ['Tdap — Midwest', '42%'], ['HPV — Southeast', '42%'], ['Influenza — Southwest', '48%']].map(([name, pct]) => (
                          <div key={name} style={{ padding: '5px 12px', borderRadius: 20, background: 'rgba(239,68,68,0.1)', border: '1px solid rgba(239,68,68,0.2)', fontSize: 12, color: '#ef4444', fontWeight: 600 }}>
                            {name} <span style={{ opacity: 0.7 }}>— {pct}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                    {/* Coverage table */}
                    <div style={{ borderRadius: 12, border: `1px solid ${c.cardBorder}`, overflow: 'hidden' }}>
                      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                        <thead>
                          <tr style={{ background: isDark ? '#0a0f1e' : '#f8fafc' }}>
                            {['VACCINE', 'REGION', 'COVERAGE %', 'PERIOD'].map(h => (
                              <th key={h} style={{ padding: '10px 16px', textAlign: 'left', fontSize: 11, fontWeight: 700, color: c.textMuted, textTransform: 'uppercase', letterSpacing: '1px', borderBottom: `1px solid ${c.cardBorder}` }}>{h}</th>
                            ))}
                          </tr>
                        </thead>
                        <tbody>
                          {[
                            ['Influenza', 'Pacific Northwest', '92%', '#22c55e', '2024 Q4'],
                            ['MMR', 'Pacific Northwest', '82%', '#22c55e', '2024 Q4'],
                            ['COVID-19', 'Pacific Northwest', '81%', '#22c55e', '2024 Q4'],
                            ['HPV', 'West Coast', '57%', '#f59e0b', '2024 Q4'],
                            ['Tdap', 'West Coast', '54%', '#f59e0b', '2024 Q4'],
                          ].map(([vaccine, region, cov, col, period]) => (
                            <tr key={`${vaccine}-${region}`} className="demo-table-row" style={{ borderBottom: `1px solid ${c.cardBorder}` }}>
                              <td style={{ padding: '11px 16px', fontWeight: 600, color: c.textPrimary }}>{vaccine}</td>
                              <td style={{ padding: '11px 16px', color: c.textSecondary }}>{region}</td>
                              <td style={{ padding: '11px 16px' }}><span style={{ padding: '3px 12px', borderRadius: 20, fontSize: 12, fontWeight: 700, background: col === '#22c55e' ? 'rgba(34,197,94,0.1)' : 'rgba(234,179,8,0.1)', color: col }}>{cov}</span></td>
                              <td style={{ padding: '11px 16px', color: c.textMuted }}>{period}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}

                {/* ── HEALTH EQUITY TAB ── */}
                {demoTab === 'Health Equity' && (
                  <div>
                    <h3 style={{ fontSize: 20, fontWeight: 700, color: c.textPrimary, marginBottom: 20 }}>Health Equity &amp; Disparities</h3>
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 14, marginBottom: 20 }}>
                      {[
                        { label: 'Equity Index', value: '9.8', color: '#00d4aa' },
                        { label: 'Disparity Flags', value: '0', color: '#22c55e' },
                        { label: 'Demographics Tracked', value: '8', color: '#3b82f6' },
                        { label: 'Regions Analyzed', value: '6', color: '#8b5cf6' },
                      ].map(s => (
                        <div key={s.label} style={{ padding: '14px 16px', borderRadius: 12, border: `1px solid ${c.cardBorder}`, background: isDark ? '#0a0f1e' : '#f8fafc' }}>
                          <div style={{ fontSize: 11, fontWeight: 600, color: c.textMuted, textTransform: 'uppercase', letterSpacing: '1px', marginBottom: 6 }}>{s.label}</div>
                          <div style={{ fontSize: 22, fontWeight: 800, color: s.color, fontFamily: "'DM Serif Display', serif" }}>{s.value}</div>
                        </div>
                      ))}
                    </div>
                    <div style={{ display: 'flex', gap: 8, marginBottom: 20 }}>
                      {['By Race', 'By Region', 'By Gender'].map((v, i) => (
                        <div key={v} style={{ padding: '7px 16px', borderRadius: 8, border: `1px solid ${i === 0 ? '#00d4aa' : c.cardBorder}`, background: i === 0 ? 'rgba(0,212,170,0.08)' : 'transparent', color: i === 0 ? '#00d4aa' : c.textMuted, fontSize: 13, fontWeight: 600, cursor: 'default' }}>{v}</div>
                      ))}
                    </div>
                    <div style={{ borderRadius: 12, border: `1px solid ${c.cardBorder}`, overflow: 'hidden' }}>
                      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                        <thead>
                          <tr style={{ background: isDark ? '#0a0f1e' : '#f8fafc' }}>
                            {['GROUP', 'TOTAL CASES', 'ACTIVE', 'CRITICAL %', 'AVG SEVERITY'].map(h => (
                              <th key={h} style={{ padding: '10px 16px', textAlign: 'left', fontSize: 11, fontWeight: 700, color: c.textMuted, textTransform: 'uppercase', letterSpacing: '1px', borderBottom: `1px solid ${c.cardBorder}` }}>{h}</th>
                            ))}
                          </tr>
                        </thead>
                        <tbody>
                          {[
                            ['Other', '41', '28', '26.8%', '2.41'],
                            ['White', '40', '32', '30.0%', '2.42'],
                            ['Black', '35', '27', '22.9%', '2.49'],
                            ['Asian', '30', '21', '16.7%', '2.13'],
                            ['Native American', '30', '23', '33.3%', '2.83'],
                          ].map(([group, total, active, crit, sev]) => (
                            <tr key={group} className="demo-table-row" style={{ borderBottom: `1px solid ${c.cardBorder}` }}>
                              <td style={{ padding: '11px 16px', fontWeight: 600, color: c.textPrimary }}>{group}</td>
                              <td style={{ padding: '11px 16px', color: c.textSecondary }}>{total}</td>
                              <td style={{ padding: '11px 16px' }}><span style={{ padding: '3px 10px', borderRadius: 20, fontSize: 11, fontWeight: 700, background: 'rgba(239,68,68,0.1)', color: '#ef4444' }}>{active}</span></td>
                              <td style={{ padding: '11px 16px', color: c.textSecondary }}>{crit}</td>
                              <td style={{ padding: '11px 16px', color: c.textMuted }}>{sev}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}

                {/* ── AI INSIGHTS TAB ── */}
                {demoTab === 'AI Insights' && (
                  <div>
                    <h3 style={{ fontSize: 20, fontWeight: 700, color: c.textPrimary, marginBottom: 20 }}>AI-Powered Insights</h3>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 12, marginBottom: 20 }}>
                      {[
                        { icon: '⚠️', text: 'Critical tuberculosis case detected requiring immediate attention. Recommend contact tracing and isolation protocols.', border: 'rgba(239,68,68,0.3)', bg: 'rgba(239,68,68,0.05)', col: '#ef4444' },
                        { icon: '⚡', text: 'Influenza A and B activity elevated. 3 severe cases in past 48hrs. Seasonal vaccination campaign recommended.', border: 'rgba(249,115,22,0.3)', bg: 'rgba(249,115,22,0.05)', col: '#f97316' },
                        { icon: '📊', text: 'Death count exceeds birth count in Northeast region. Potential demographic shift warranting further epidemiological investigation.', border: 'rgba(59,130,246,0.3)', bg: 'rgba(59,130,246,0.05)', col: '#3b82f6' },
                      ].map((ins, i) => (
                        <div key={i} style={{ padding: '16px 18px', borderRadius: 12, border: `1px solid ${ins.border}`, background: ins.bg, display: 'flex', gap: 14, alignItems: 'flex-start' }}>
                          <span style={{ fontSize: 20, marginTop: 1 }}>{ins.icon}</span>
                          <p style={{ fontSize: 14, lineHeight: 1.6, color: c.textPrimary, margin: 0 }}>{ins.text}</p>
                        </div>
                      ))}
                    </div>
                    {/* Summary */}
                    <div style={{ padding: '16px 18px', borderRadius: 12, border: `1px solid ${c.cardBorder}`, background: isDark ? '#0a0f1e' : '#f8fafc', marginBottom: 16 }}>
                      <div style={{ fontSize: 12, fontWeight: 700, color: '#00d4aa', textTransform: 'uppercase', letterSpacing: '1px', marginBottom: 10 }}>Summary</div>
                      <p style={{ fontSize: 14, lineHeight: 1.7, color: c.textSecondary, margin: 0 }}>
                        Current surveillance data indicates elevated disease activity across multiple regions. Tuberculosis and Influenza remain the primary concerns, with critical case clusters requiring coordinated public health response. Vaccination coverage gaps in the Southwest region suggest targeted immunization campaigns may reduce near-term risk.
                      </p>
                    </div>
                    {/* Fake input bar */}
                    <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
                      <div style={{ flex: 1, padding: '12px 16px', borderRadius: 10, border: `1px solid ${c.cardBorder}`, background: isDark ? '#0f172a' : '#ffffff', fontSize: 14, color: c.textMuted, cursor: 'default' }}>
                        Ask AI a question...
                      </div>
                      <div style={{ padding: '12px 22px', borderRadius: 10, background: 'linear-gradient(135deg, #00d4aa, #00b894)', color: '#020617', fontSize: 14, fontWeight: 700, cursor: 'default', whiteSpace: 'nowrap' }}>
                        Generate Insights
                      </div>
                    </div>
                  </div>
                )}

                {/* Bottom CTA */}
                <div style={{ marginTop: 28, paddingTop: 20, borderTop: `1px solid ${c.cardBorder}`, textAlign: 'center' }}>
                  <Link to="/register" className="cta-btn cta-primary" style={{ fontSize: 15, display: 'inline-flex' }}>
                    Sign Up Free to Access Full Platform <span style={{ fontSize: 18, marginLeft: 6 }}>→</span>
                  </Link>
                </div>
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      {/* ═══ STATS ═══ */}
      <section style={{ padding: '60px 48px', borderTop: `1px solid ${c.statsBorder}`, borderBottom: `1px solid ${c.statsBorder}`, background: c.statsBg }}>
        <div style={{ maxWidth: 1280, margin: '0 auto' }}>
          <div className="stats-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 48, textAlign: 'center' }}>
            {[[50, '+', 'Health Departments'], [2, 'M+', 'Records Tracked'], [15, '+', 'Surveillance Modules'], [99.9, '%', 'Uptime SLA']].map(([num, suf, label]) => (
              <div key={label}>
                <div style={{ fontFamily: "'DM Serif Display', serif", fontSize: 48, fontWeight: 400, color: c.textPrimary }}><Counter end={num} suffix={suf} /></div>
                <div style={{ fontSize: 14, color: c.textMuted, fontWeight: 600, marginTop: 8, textTransform: 'uppercase', letterSpacing: '1px' }}>{label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ═══ FEATURES ═══ */}
      <section id="features" style={{ padding: '120px 48px', background: c.pageBg }}>
        <div style={{ maxWidth: 1280, margin: '0 auto' }}>
          <Reveal>
            <div style={{ textAlign: 'center', marginBottom: 80 }}>
              <span style={{ fontSize: 13, fontWeight: 700, letterSpacing: '3px', textTransform: 'uppercase', color: c.sectionLabel }}>Why EpiSurveillance</span>
              <h2 className="section-title" style={{ fontFamily: "'DM Serif Display', serif", fontSize: 52, fontWeight: 400, marginTop: 16, letterSpacing: '-1px', color: c.textPrimary }}>
                Built for the Teams That<br />Protect Public Health
              </h2>
              <p style={{ fontSize: 18, color: c.textMuted, maxWidth: 640, margin: '20px auto 0', lineHeight: 1.7 }}>
                From disease outbreak detection to health equity analysis — every tool your surveillance team needs, unified in one powerful platform.
              </p>
            </div>
          </Reveal>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 24 }}>
            {[
              { icon: '⚡', title: 'Real-Time Monitoring', desc: 'Live dashboards update as data flows in. No batch processing, no delays. See disease patterns as they emerge.', color: '#f59e0b' },
              { icon: '🧠', title: 'AI-Powered Intelligence', desc: 'Machine learning detects anomalies, predicts outbreaks, and generates reports that would take analysts weeks to produce.', color: '#8b5cf6' },
              { icon: '🏢', title: 'Multi-Tenant Architecture', desc: 'Create isolated workspaces for every jurisdiction. Virginia, Florida, Gujarat — each gets their own fully independent system.', color: '#00d4aa' },
              { icon: '🔒', title: 'Enterprise Security', desc: 'End-to-end encryption, role-based access, comprehensive audit logging, and compliance-ready architecture.', color: '#ef4444' },
              { icon: '📱', title: 'Mobile-Ready Interface', desc: 'Field workers can report cases, submit assessments, and check alerts from any device, anywhere.', color: '#3b82f6' },
              { icon: '🔗', title: 'Seamless Integration', desc: 'CSV bulk imports, API access, and structured data exchange for interoperability with existing health IT systems.', color: '#ec4899' },
            ].map((f, i) => (
              <Reveal key={f.title} delay={i * 0.08}>
                <div className="module-card" style={{ height: '100%' }}>
                  <div style={{ width: 56, height: 56, borderRadius: 16, background: `${f.color}12`, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 28, marginBottom: 20 }}>{f.icon}</div>
                  <h3 style={{ fontSize: 20, fontWeight: 700, color: c.textPrimary, marginBottom: 12 }}>{f.title}</h3>
                  <p style={{ fontSize: 15, lineHeight: 1.7, color: c.textMuted }}>{f.desc}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* ═══ HOW IT WORKS ═══ */}
      <section id="how-it-works" style={{ padding: '120px 48px', background: c.pageBg2 }}>
        <div style={{ maxWidth: 1280, margin: '0 auto' }}>
          <Reveal>
            <div style={{ textAlign: 'center', marginBottom: 80 }}>
              <span style={{ fontSize: 13, fontWeight: 700, letterSpacing: '3px', textTransform: 'uppercase', color: c.sectionLabel }}>How It Works</span>
              <h2 className="section-title" style={{ fontFamily: "'DM Serif Display', serif", fontSize: 52, fontWeight: 400, marginTop: 16, letterSpacing: '-1px', color: c.textPrimary }}>
                Up and Running in<br /><span className="shimmer-text">Under 10 Minutes</span>
              </h2>
            </div>
          </Reveal>

          <div className="steps-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 24 }}>
            {steps.map((s, i) => (
              <Reveal key={s.num} delay={i * 0.12}>
                <div className="step-card">
                  <div style={{ fontFamily: "'DM Serif Display', serif", fontSize: 64, fontWeight: 400, color: c.stepNum, lineHeight: 1, marginBottom: 24 }}>{s.num}</div>
                  <h3 style={{ fontSize: 22, fontWeight: 700, color: c.textPrimary, marginBottom: 12 }}>{s.title}</h3>
                  <p style={{ fontSize: 15, lineHeight: 1.7, color: c.textMuted }}>{s.desc}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* ═══ MODULES ═══ */}
      <section id="modules" style={{ padding: '120px 48px', background: c.pageBg }}>
        <div style={{ maxWidth: 1280, margin: '0 auto' }}>
          <Reveal>
            <div style={{ textAlign: 'center', marginBottom: 80 }}>
              <span style={{ fontSize: 13, fontWeight: 700, letterSpacing: '3px', textTransform: 'uppercase', color: c.sectionLabel }}>Complete Platform</span>
              <h2 className="section-title" style={{ fontFamily: "'DM Serif Display', serif", fontSize: 52, fontWeight: 400, marginTop: 16, letterSpacing: '-1px', color: c.textPrimary }}>
                15 Modules. One Platform.<br />Zero Gaps.
              </h2>
            </div>
          </Reveal>
          <div className="modules-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 20 }}>
            {modules.map((m, i) => (
              <Reveal key={m.title} delay={i * 0.05}>
                <div className="module-card">
                  <div style={{ fontSize: 32, marginBottom: 16 }}>{m.icon}</div>
                  <h3 style={{ fontSize: 18, fontWeight: 700, color: c.textPrimary, marginBottom: 10 }}>{m.title}</h3>
                  <p style={{ fontSize: 14, lineHeight: 1.7, color: c.textMuted }}>{m.desc}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* ═══ WORKSPACE SHOWCASE ═══ */}
      <section style={{ padding: '120px 48px', background: c.pageBg2, position: 'relative', overflow: 'hidden' }}>
        <div style={{ position: 'absolute', inset: 0, background: `radial-gradient(ellipse at center, ${c.orbGreen} 0%, transparent 60%)` }} />
        <div style={{ maxWidth: 1280, margin: '0 auto', position: 'relative', zIndex: 2 }}>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 80, alignItems: 'center' }}>
            <Reveal>
              <div>
                <span style={{ fontSize: 13, fontWeight: 700, letterSpacing: '3px', textTransform: 'uppercase', color: c.sectionLabel }}>Multi-Tenant Workspaces</span>
                <h2 style={{ fontFamily: "'DM Serif Display', serif", fontSize: 46, fontWeight: 400, marginTop: 16, letterSpacing: '-1px', color: c.textPrimary, lineHeight: 1.15 }}>
                  One Platform,<br />Infinite Jurisdictions
                </h2>
                <p style={{ fontSize: 17, lineHeight: 1.8, color: c.textSecondary, marginTop: 24 }}>
                  Virginia needs a surveillance system? Create a workspace. Florida sends a request? Spin up another. India's Gujarat state needs coverage? Deploy in minutes.
                </p>
                <p style={{ fontSize: 17, lineHeight: 1.8, color: c.textSecondary, marginTop: 16 }}>
                  Each workspace is a fully independent system with its own administrator, user management, data, reports, and AI analytics — completely isolated yet centrally manageable.
                </p>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 16, marginTop: 32 }}>
                  {['Full feature parity across all workspaces', 'Independent admin accounts with dedicated login', 'Data isolation by default — zero cross-contamination', 'Super admin console for centralized oversight'].map(t => (
                    <div key={t} style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                      <div style={{ width: 24, height: 24, borderRadius: 8, background: 'rgba(0,212,170,0.1)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                        <span style={{ color: '#00d4aa', fontSize: 14 }}>✓</span>
                      </div>
                      <span style={{ fontSize: 15, color: c.textSecondary }}>{t}</span>
                    </div>
                  ))}
                </div>
              </div>
            </Reveal>
            <Reveal delay={0.2}>
              <div style={{ position: 'relative' }}>
                {[
                  { name: 'Virginia', type: 'State', color: '#10b981', users: 24, cases: '12,847' },
                  { name: 'Florida', type: 'State', color: '#3b82f6', users: 31, cases: '18,392' },
                  { name: 'Gujarat, India', type: 'International', color: '#8b5cf6', users: 18, cases: '9,241' },
                ].map((ws, i) => (
                  <div key={ws.name} style={{
                    padding: 24, background: c.wsCardBg, border: `1px solid ${c.cardBorder}`, borderRadius: 16,
                    backdropFilter: 'blur(10px)', marginBottom: 16, marginLeft: i * 20,
                    animation: `float ${6 + i}s ease-in-out infinite ${i * 0.5}s`
                  }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 12 }}>
                      <div style={{ width: 36, height: 36, borderRadius: 10, background: `${ws.color}20`, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 18 }}>🌍</div>
                      <div>
                        <div style={{ fontWeight: 700, fontSize: 16, color: c.textPrimary }}>{ws.name}</div>
                        <div style={{ fontSize: 11, color: ws.color, fontWeight: 600 }}>{ws.type}</div>
                      </div>
                    </div>
                    <div style={{ display: 'flex', gap: 24, fontSize: 13 }}>
                      <span style={{ color: c.textMuted }}>Users: <span style={{ color: c.textSecondary, fontWeight: 600 }}>{ws.users}</span></span>
                      <span style={{ color: c.textMuted }}>Cases: <span style={{ color: c.textSecondary, fontWeight: 600 }}>{ws.cases}</span></span>
                      <span style={{ color: '#10b981', fontWeight: 600 }}>● Active</span>
                    </div>
                  </div>
                ))}
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      {/* ═══ CTA BANNER ═══ */}
      <section style={{ padding: '100px 48px', background: 'linear-gradient(135deg, #00d4aa 0%, #00b894 40%, #00a67d 100%)', position: 'relative', overflow: 'hidden' }}>
        <div style={{ position: 'absolute', inset: 0, background: 'radial-gradient(ellipse at top right, rgba(255,255,255,0.1) 0%, transparent 50%)' }} />
        <div style={{ maxWidth: 800, margin: '0 auto', textAlign: 'center', position: 'relative', zIndex: 2 }}>
          <Reveal>
            <h2 style={{ fontFamily: "'DM Serif Display', serif", fontSize: 50, fontWeight: 400, color: '#020617', letterSpacing: '-1px', marginBottom: 20 }}>
              Ready to Modernize Your Surveillance?
            </h2>
            <p style={{ fontSize: 18, color: '#065f46', lineHeight: 1.7, marginBottom: 40 }}>
              Join the growing network of health departments using EpiSurveillance to detect threats faster, respond smarter, and protect communities better.
            </p>
            <div style={{ display: 'flex', gap: 16, justifyContent: 'center', flexWrap: 'wrap' }}>
              <Link to="/register" className="cta-btn" style={{ background: '#020617', color: '#00d4aa', fontSize: 17, padding: '16px 40px' }}>
                Start Free Trial <span style={{ fontSize: 20 }}>→</span>
              </Link>
              <button className="cta-btn" onClick={() => scrollTo('contact')} style={{ background: 'transparent', color: '#020617', border: '2px solid #020617', fontSize: 17, padding: '16px 40px' }}>
                Contact Sales
              </button>
            </div>
          </Reveal>
        </div>
      </section>

      {/* ═══ FAQ ═══ */}
      <section id="faq" style={{ padding: '120px 48px', background: c.pageBg }}>
        <div style={{ maxWidth: 800, margin: '0 auto' }}>
          <Reveal>
            <div style={{ textAlign: 'center', marginBottom: 60 }}>
              <span style={{ fontSize: 13, fontWeight: 700, letterSpacing: '3px', textTransform: 'uppercase', color: c.sectionLabel }}>FAQ</span>
              <h2 className="section-title" style={{ fontFamily: "'DM Serif Display', serif", fontSize: 46, fontWeight: 400, marginTop: 16, letterSpacing: '-1px', color: c.textPrimary }}>
                Frequently Asked Questions
              </h2>
            </div>
          </Reveal>
          {faqs.map((f, i) => (
            <Reveal key={i} delay={i * 0.06}>
              <FAQItem q={f.q} a={f.a} borderColor={c.cardBorder} textColor={c.textPrimary} mutedColor={c.textSecondary} accentColor={c.accent} />
            </Reveal>
          ))}
        </div>
      </section>

      {/* ═══ CONTACT ═══ */}
      <section id="contact" style={{ padding: '120px 48px', background: c.pageBg2 }}>
        <div style={{ maxWidth: 1280, margin: '0 auto' }}>
          <Reveal>
            <div style={{ textAlign: 'center', marginBottom: 60 }}>
              <span style={{ fontSize: 13, fontWeight: 700, letterSpacing: '3px', textTransform: 'uppercase', color: c.sectionLabel }}>Get In Touch</span>
              <h2 className="section-title" style={{ fontFamily: "'DM Serif Display', serif", fontSize: 46, fontWeight: 400, marginTop: 16, letterSpacing: '-1px', color: c.textPrimary }}>
                Let's Build the Future of<br />Public Health Together
              </h2>
            </div>
          </Reveal>

          <div className="contact-grid" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 60 }}>
            <Reveal>
              <div>
                <p style={{ fontSize: 17, lineHeight: 1.8, color: c.textSecondary, marginBottom: 40 }}>
                  Whether you're a state health department, a county agency, or a national organization — we'd love to show you how EpiSurveillance can transform your surveillance operations.
                </p>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 28 }}>
                  {[
                    { icon: '📧', label: 'Email', value: 'support@episurveillance.com', href: 'mailto:support@episurveillance.com' },
                    { icon: '📞', label: 'Phone', value: '(540) 322-7406', href: 'tel:5403227406' },
                    { icon: '📍', label: 'Address', value: '2003 Plank Road #1283\nFredericksburg, VA 22401', href: null },
                  ].map(ct => (
                    <div key={ct.label} style={{ display: 'flex', gap: 16, alignItems: 'flex-start' }}>
                      <div style={{ width: 48, height: 48, borderRadius: 14, background: 'rgba(0,212,170,0.08)', border: '1px solid rgba(0,212,170,0.15)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 22, flexShrink: 0 }}>{ct.icon}</div>
                      <div>
                        <div style={{ fontSize: 12, fontWeight: 700, letterSpacing: '1px', textTransform: 'uppercase', color: c.textMuted, marginBottom: 4 }}>{ct.label}</div>
                        {ct.href ? (
                          <a href={ct.href} style={{ fontSize: 16, color: c.textPrimary, textDecoration: 'none', fontWeight: 600 }}>{ct.value}</a>
                        ) : (
                          <div style={{ fontSize: 16, color: c.textPrimary, fontWeight: 600, whiteSpace: 'pre-line' }}>{ct.value}</div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </Reveal>

            <Reveal delay={0.15}>
              <div style={{ padding: 40, background: isDark ? 'rgba(15,23,42,0.6)' : 'rgba(255,255,255,0.8)', border: `1px solid ${c.cardBorder}`, borderRadius: 24, backdropFilter: 'blur(10px)', boxShadow: isDark ? 'none' : '0 4px 24px rgba(0,0,0,0.06)' }}>
                {demoSubmitted ? (
                  <div style={{ textAlign: 'center', padding: '40px 0' }}>
                    <div style={{ width: 72, height: 72, borderRadius: '50%', background: 'rgba(0,212,170,0.12)', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 24px', fontSize: 36 }}>✓</div>
                    <h3 style={{ fontSize: 26, fontWeight: 800, color: c.textPrimary, marginBottom: 12, fontFamily: "'DM Serif Display', serif" }}>Demo Request Submitted!</h3>
                    <p style={{ fontSize: 16, color: c.textSecondary, lineHeight: 1.7, maxWidth: 360, margin: '0 auto 8px' }}>
                      Thank you, <span style={{ color: '#00d4aa', fontWeight: 600 }}>{demoForm.firstName}</span>! We've received your request.
                    </p>
                    <p style={{ fontSize: 15, color: c.textMuted, lineHeight: 1.7, maxWidth: 360, margin: '0 auto 32px' }}>
                      Our team will reach out to <span style={{ color: c.textSecondary }}>{demoForm.email}</span> within 24 hours.
                    </p>
                    <div style={{ display: 'flex', gap: 16, justifyContent: 'center', flexWrap: 'wrap' }}>
                      <a href="mailto:support@episurveillance.com" className="cta-btn cta-primary" style={{ padding: '12px 28px', fontSize: 14, textDecoration: 'none' }}>Email Us Directly</a>
                      <button className="cta-btn cta-secondary" style={{ padding: '12px 28px', fontSize: 14 }} onClick={() => { setDemoSubmitted(false); setDemoForm({ firstName: '', lastName: '', email: '', organization: '', orgType: '', message: '', website: '' }); refreshCaptcha(); }}>Submit Another</button>
                    </div>
                  </div>
                ) : (
                  <>
                    <h3 style={{ fontSize: 22, fontWeight: 700, color: c.textPrimary, marginBottom: 24 }}>Request a Demo</h3>
                    <form onSubmit={handleDemoSubmit} style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
                      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                        <input placeholder="First Name *" required value={demoForm.firstName} onChange={e => setDemoForm({...demoForm, firstName: e.target.value})} style={inputStyle} />
                        <input placeholder="Last Name" value={demoForm.lastName} onChange={e => setDemoForm({...demoForm, lastName: e.target.value})} style={inputStyle} />
                      </div>
                      <input placeholder="Work Email *" type="email" required value={demoForm.email} onChange={e => setDemoForm({...demoForm, email: e.target.value})} style={inputStyle} />
                      <input placeholder="Organization" value={demoForm.organization} onChange={e => setDemoForm({...demoForm, organization: e.target.value})} style={inputStyle} />
                      <select value={demoForm.orgType} onChange={e => setDemoForm({...demoForm, orgType: e.target.value})} style={{ ...inputStyle, color: demoForm.orgType ? c.inputText : c.selectText }}>
                        <option value="">Organization Type</option>
                        <option value="State Health Department">State Health Department</option>
                        <option value="County / Local Agency">County / Local Agency</option>
                        <option value="National Organization">National Organization</option>
                        <option value="International Agency">International Agency</option>
                        <option value="Hospital / Health System">Hospital / Health System</option>
                        <option value="Academic / Research">Academic / Research</option>
                        <option value="Other">Other</option>
                      </select>
                      <textarea placeholder="Tell us about your surveillance needs..." rows={4} value={demoForm.message} onChange={e => setDemoForm({...demoForm, message: e.target.value})} style={{ ...inputStyle, resize: 'vertical' }} />

                      {/* Honeypot field — invisible to real users, catches basic bots */}
                      <input
                        type="text"
                        name="website"
                        value={demoForm.website}
                        onChange={e => setDemoForm({...demoForm, website: e.target.value})}
                        tabIndex={-1}
                        autoComplete="off"
                        aria-hidden="true"
                        style={{ position: 'absolute', left: '-9999px', width: 1, height: 1, opacity: 0 }}
                      />

                      <div>
                        <label style={{ display: 'block', fontSize: 13, fontWeight: 600, color: c.textSecondary, marginBottom: 8 }}>
                          Quick check: {captcha.question || 'Loading…'} *
                        </label>
                        <input
                          type="text"
                          inputMode="numeric"
                          placeholder="Your answer"
                          required
                          value={captchaAnswer}
                          onChange={e => setCaptchaAnswer(e.target.value)}
                          style={inputStyle}
                        />
                      </div>

                      {demoError && <div style={{ color: '#f87171', fontSize: 13, fontWeight: 600 }}>{demoError}</div>}
                      <button type="submit" disabled={demoSubmitting} className="cta-btn cta-primary" style={{ width: '100%', justifyContent: 'center', fontSize: 16, opacity: demoSubmitting ? 0.7 : 1 }}>
                        {demoSubmitting ? 'Submitting...' : 'Request Demo →'}
                      </button>
                    </form>
                  </>
                )}
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      {/* ═══ FOOTER ═══ */}
      <footer style={{ padding: '80px 48px 40px', background: c.footerBg, borderTop: `1px solid ${c.footerBorder}` }}>
        <div style={{ maxWidth: 1280, margin: '0 auto' }}>
          <div className="footer-grid" style={{ display: 'grid', gridTemplateColumns: '2fr 1fr 1fr 1fr', gap: 60, marginBottom: 60 }}>
            <div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 20 }}>
                <div style={{ width: 42, height: 42, borderRadius: 12, background: 'linear-gradient(135deg, #00d4aa, #00b894)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 800, fontSize: 16, color: '#020617', fontFamily: 'DM Serif Display, serif' }}>ES</div>
                <div>
                  <div style={{ fontWeight: 800, fontSize: 18, color: '#f1f5f9' }}>EpiSurveillance</div>
                  <div style={{ fontSize: 10, fontWeight: 600, letterSpacing: '2px', color: '#00d4aa', textTransform: 'uppercase' }}>Modern Disease Surveillance</div>
                </div>
              </div>
              <p style={{ fontSize: 15, lineHeight: 1.8, color: '#64748b', maxWidth: 360, marginBottom: 20 }}>
                The modern era of disease surveillance. Empowering public health teams worldwide with AI-powered tools, real-time analytics, and multi-jurisdiction workspaces.
              </p>
              <div style={{ fontSize: 14, color: '#475569' }}>
                2003 Plank Road #1283<br />Fredericksburg, VA 22401
              </div>
            </div>
            <div>
              <h4 style={{ fontSize: 13, fontWeight: 700, letterSpacing: '2px', textTransform: 'uppercase', color: '#64748b', marginBottom: 20 }}>Platform</h4>
              {[['Disease Surveillance','/platform/disease-surveillance'],['Vaccination Tracking','/platform/vaccination-tracking'],['Vital Statistics','/platform/vital-statistics'],['SDOH Tracker','/platform/sdoh-tracker'],['Health Equity','/platform/health-equity'],['AI Insights','/platform/ai-insights'],['Reports & Analytics','/platform/reports-analytics'],['Pricing','/pricing']].map(([label,path]) => (
                <div key={label} style={{ marginBottom: 12 }}><Link to={path} className="nav-link" style={{ fontSize: 14, color: '#94a3b8', textDecoration: 'none' }}>{label}</Link></div>
              ))}
            </div>
            <div>
              <h4 style={{ fontSize: 13, fontWeight: 700, letterSpacing: '2px', textTransform: 'uppercase', color: '#64748b', marginBottom: 20 }}>Company</h4>
              {[['About Us','/company/about'],['Careers','/company/careers'],['Blog','/company/blog'],['Press','/company/press'],['Partners','/company/partners'],['Security','/company/security'],['Compliance','/company/compliance']].map(([label,path]) => (
                <div key={label} style={{ marginBottom: 12 }}><Link to={path} className="nav-link" style={{ fontSize: 14, color: '#94a3b8', textDecoration: 'none' }}>{label}</Link></div>
              ))}
            </div>
            <div>
              <h4 style={{ fontSize: 13, fontWeight: 700, letterSpacing: '2px', textTransform: 'uppercase', color: '#64748b', marginBottom: 20 }}>Support</h4>
              {[['Documentation','/support/documentation'],['API Reference','/support/api-reference'],['Help Center','/support/help-center'],['Training','/support/training'],['Community','/support/community']].map(([label,path]) => (
                <div key={label} style={{ marginBottom: 12 }}><Link to={path} className="nav-link" style={{ fontSize: 14, color: '#94a3b8', textDecoration: 'none' }}>{label}</Link></div>
              ))}
              <div style={{ marginTop: 24 }}>
                <div style={{ fontSize: 14, color: '#94a3b8', marginBottom: 8 }}>
                  <a href="mailto:support@episurveillance.com" style={{ color: '#94a3b8', textDecoration: 'none' }}>support@episurveillance.com</a>
                </div>
                <div style={{ fontSize: 14, color: '#94a3b8' }}>
                  <a href="tel:5403227406" style={{ color: '#94a3b8', textDecoration: 'none' }}>(540) 322-7406</a>
                </div>
              </div>
            </div>
          </div>

          <div style={{ borderTop: '1px solid #1e293b', paddingTop: 32, display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 16 }}>
            <div style={{ fontSize: 13, color: '#475569' }}>
              © {new Date().getFullYear()} EpiSurveillance. All rights reserved. The Modern Era of Disease Surveillance.
            </div>
            <div style={{ display: 'flex', gap: 24, fontSize: 13 }}>
              {[['Privacy Policy','/legal/privacy'],['Terms of Service','/legal/terms'],['Cookie Policy','/legal/cookies'],['HIPAA','/company/compliance']].map(([label,path]) => (
                <Link key={label} to={path} style={{ color: '#64748b', textDecoration: 'none', fontSize: 13 }}>{label}</Link>
              ))}
              <button onClick={openCookieSettings} style={{ background: 'none', border: 'none', padding: 0, color: '#64748b', fontSize: 13, cursor: 'pointer', fontFamily: 'inherit' }}>Cookie settings</button>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default LandingPage;
